import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { getClassGroups, updateClassGroupsConfig, addClassGroup, removeClassGroup, assignStudentToClassGroup, getStudents, uploadGroupImage, deleteGroupImage, getCoreClassesManage, updateCoreClasses, setCoreClassActive, getAssessmentRubric, updateAssessmentRubric } from "../../lib/api";
import { firstName, sortByFirst } from "../../lib/names";

function CoreClassesSection() {
  // Each item: { name, active }. Order preserved.
  const [items, setItems] = useState([]);
  const [newClass, setNewClass] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = () => getCoreClassesManage()
    .then(res => setItems(Array.isArray(res.data?.classes) ? res.data.classes : []))
    .catch(() => {});

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  // Persist the full ordered name list (add / remove / reorder), preserving active state server-side.
  const saveNames = async (nextItems) => {
    try {
      await updateCoreClasses(nextItems.map((i) => i.name));
      setItems(nextItems);
      return true;
    } catch { toast.error("Failed to save"); return false; }
  };

  const handleAdd = async () => {
    const name = newClass.trim();
    if (!name) return;
    if (items.some((i) => i.name.toLowerCase() === name.toLowerCase())) { toast.error("Already exists"); return; }
    const ok = await saveNames([...items, { name, active: true }]);
    if (ok) { setNewClass(""); toast.success("Class added"); }
  };

  const handleRemove = async (name) => {
    if (!window.confirm(`Permanently delete "${name}"? Historical records keep this class name; this only removes it from the list. Tip: deactivate instead to hide it from new records while keeping it here.`)) return;
    const ok = await saveNames(items.filter((i) => i.name !== name));
    if (ok) toast.success("Class removed");
  };

  const handleReorder = async (idx, dir) => {
    const arr = [...items];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    await saveNames(arr);
  };

  const handleToggleActive = async (name, active) => {
    try {
      await setCoreClassActive(name, active);
      setItems((prev) => prev.map((i) => (i.name === name ? { ...i, active } : i)));
      toast.success(active ? "Class reactivated" : "Class deactivated");
    } catch { toast.error("Failed to update"); }
  };

  if (loading) return <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>;

  const activeItems = items.filter((i) => i.active);
  const inactiveItems = items.filter((i) => !i.active);

  return (
    <Card className="mb-6" data-testid="core-classes-section">
      <CardHeader>
        <CardTitle>Core Classes</CardTitle>
        <CardDescription>These classes appear in the assessment dropdown for teachers and students. Deactivate a class to hide it from all dropdowns and new records while keeping every past assessment and report intact. Select "Other" for anything not listed.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="active-core-classes">
            {activeItems.length === 0 && <span className="text-sm text-slate-400 italic">No active classes.</span>}
            {activeItems.map((cls) => {
              const i = items.indexOf(cls);
              return (
              <div key={cls.name} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-50 text-blue-800 text-sm font-medium border border-blue-200" data-testid={`core-class-${cls.name}`}>
                <button onClick={() => handleReorder(i, -1)} className="text-blue-400 hover:text-blue-600" title="Move up" disabled={i === 0}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                </button>
                {cls.name}
                <button onClick={() => handleReorder(i, 1)} className="text-blue-400 hover:text-blue-600" title="Move down" disabled={i === items.length - 1}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </button>
                <button onClick={() => handleToggleActive(cls.name, false)} className="text-blue-400 hover:text-amber-600 ml-1" title="Deactivate (hide from dropdowns)" data-testid={`deactivate-core-class-${cls.name}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>
                </button>
              </div>
            );})}
          </div>
          <div className="flex gap-2">
            <Input value={newClass} onChange={(e) => setNewClass(e.target.value)} placeholder="New class name..." className="w-56" data-testid="new-core-class-input"
              onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            />
            <Button size="sm" onClick={handleAdd} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-core-class-btn">Add Class</Button>
          </div>

          {inactiveItems.length > 0 && (
            <div className="pt-3 border-t" data-testid="inactive-core-classes">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Inactive classes ({inactiveItems.length})</p>
              <p className="text-xs text-slate-400 mb-2">Hidden from all dropdowns and new records. Past records stay visible in reports and student files. Reactivate anytime.</p>
              <div className="flex flex-wrap gap-2">
                {inactiveItems.map((cls) => (
                  <div key={cls.name} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-slate-500 text-sm font-medium border border-slate-200 line-through" data-testid={`inactive-core-class-${cls.name}`}>
                    {cls.name}
                    <button onClick={() => handleToggleActive(cls.name, true)} className="no-underline text-emerald-600 hover:text-emerald-800 text-xs font-semibold" title="Reactivate" data-testid={`reactivate-core-class-${cls.name}`}>Reactivate</button>
                    <button onClick={() => handleRemove(cls.name)} className="text-slate-400 hover:text-red-600" title="Delete permanently" data-testid={`delete-core-class-${cls.name}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GroupSection({ period, groups, allStudents, editingClassGroup, setEditingClassGroup, editClassGroupName, setEditClassGroupName, classGroups, fetchData }) {
  const color = period === "morning" ? "#3B82F6" : "#10B981";
  const badgeClass = period === "morning" ? "bg-blue-50 text-blue-800" : "bg-green-50 text-green-800";
  const imageInputRefs = useRef({});

  const handleImageUpload = async (groupKey, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      e.target.value = "";
      return;
    }
    try {
      await uploadGroupImage(period, groupKey, file);
      toast.success("Group image uploaded");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload image");
    }
    e.target.value = "";
  };

  const handleImageDelete = async (groupKey) => {
    try {
      await deleteGroupImage(period, groupKey);
      toast.success("Group image removed");
      fetchData();
    } catch {
      toast.error("Failed to remove image");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#00305E]">{period === "morning" ? "Morning" : "Afternoon"} Groups</h3>
        <Button size="sm" onClick={async () => {
          try { await addClassGroup(period); toast.success(`${period} group added`); fetchData(); } catch { toast.error("Failed to add group"); }
        }} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid={`add-${period}-group-btn`}>Add Group</Button>
      </div>
      <div className="space-y-3">
        {groups?.map((group) => (
          <div key={group.key} className="border rounded-lg p-4" style={{ borderLeftWidth: '4px', borderLeftColor: color }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {/* Group Image */}
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  ref={el => imageInputRefs.current[group.key] = el}
                  onChange={(e) => handleImageUpload(group.key, e)}
                  data-testid={`${period}-group-image-input-${group.key}`}
                />
                <div className="relative group/img shrink-0">
                  {group.image ? (
                    <div className="relative">
                      <img
                        src={group.image}
                        alt={group.name}
                        className="w-12 h-12 rounded-lg object-cover border"
                        data-testid={`${period}-group-image-${group.key}`}
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <button
                          onClick={() => imageInputRefs.current[group.key]?.click()}
                          className="p-0.5 text-white hover:text-blue-200"
                          title="Change"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                        </button>
                        <button
                          onClick={() => handleImageDelete(group.key)}
                          className="p-0.5 text-white hover:text-red-300"
                          title="Remove"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => imageInputRefs.current[group.key]?.click()}
                      className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors"
                      title="Upload group image"
                      data-testid={`${period}-group-upload-image-${group.key}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                    </button>
                  )}
                </div>

                {editingClassGroup === `${period}-${group.key}` ? (
                  <div className="flex items-center gap-2">
                    <Input value={editClassGroupName} onChange={(e) => setEditClassGroupName(e.target.value)} className="w-48" data-testid={`${period}-group-name-input-${group.key}`} />
                    <Button size="sm" onClick={async () => {
                      const config = { ...classGroups.config };
                      const configKey = `${period}_groups`;
                      config[configKey] = { ...config[configKey] };
                      config[configKey][group.key] = { ...config[configKey][group.key], name: editClassGroupName };
                      await updateClassGroupsConfig({ [configKey]: config[configKey] });
                      toast.success("Group name updated"); setEditingClassGroup(null); fetchData();
                    }} className="bg-[#00305E]">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingClassGroup(null)}>Cancel</Button>
                  </div>
                ) : (
                  <>
                    <h4 className="font-medium">{group.name}</h4>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingClassGroup(`${period}-${group.key}`); setEditClassGroupName(group.name); }} data-testid={`edit-${period}-group-${group.key}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{group.students?.length || 0} students</span>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                  onClick={async () => { if (window.confirm(`Remove ${group.name}?`)) { await removeClassGroup(period, group.key); toast.success("Group removed"); fetchData(); } }}
                  data-testid={`remove-${period}-group-${group.key}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {sortByFirst(group.students)?.map((s) => (
                <span key={s.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${badgeClass}`}>
                  {firstName(allStudents.find(x => x.id === s.id) || s)}
                  <button onClick={async () => { await assignStudentToClassGroup(s.id, period, null); toast.success("Student removed"); fetchData(); }} className="hover:text-red-600">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                  </button>
                </span>
              ))}
            </div>
            <Select value="" onValueChange={async (studentId) => { if (studentId) { await assignStudentToClassGroup(studentId, period, group.key); toast.success("Student assigned"); fetchData(); } }}>
              <SelectTrigger className="w-64" data-testid={`add-student-${period}-${group.key}`}><SelectValue placeholder="Add student..." /></SelectTrigger>
              <SelectContent>
                {sortByFirst(allStudents.filter(s => !group.students?.some(gs => gs.id === s.id))).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{firstName(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

function RubricImageSection() {
  const [image, setImage] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    getAssessmentRubric().then((res) => setImage(res.data?.image || "")).catch(() => {});
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); e.target.value = ""; return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setSaving(true);
      try {
        await updateAssessmentRubric(dataUrl);
        setImage(dataUrl);
        toast.success("Rubric image saved");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to save image");
      } finally {
        setSaving(false);
        e.target.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await updateAssessmentRubric("");
      setImage("");
      toast.success("Rubric image removed");
    } catch { toast.error("Failed to remove image"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="rubric-image-section">
      <CardHeader>
        <CardTitle>Assessment Rubric Image</CardTitle>
        <CardDescription>Upload a reference image (e.g., what a 1–4 score means for each category). It appears at the top of every assessment score screen so staff can see it before scrolling through students.</CardDescription>
      </CardHeader>
      <CardContent>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="rubric-image-input" />
        {image ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 flex justify-center overflow-hidden max-w-2xl">
              <img src={image} alt="Assessment rubric" className="max-h-80 w-auto object-contain" data-testid="rubric-image-preview" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => inputRef.current?.click()} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="rubric-image-replace-btn">
                {saving ? "Saving..." : "Replace Image"}
              </Button>
              <Button size="sm" variant="outline" onClick={handleRemove} disabled={saving} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="rubric-image-remove-btn">
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="rubric-image-upload-btn">
            {saving ? "Saving..." : "Upload Rubric Image"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function ClassGroupsTab() {
  const [classGroups, setClassGroups] = useState({ morning_groups: [], afternoon_groups: [] });
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClassGroup, setEditingClassGroup] = useState(null);
  const [editClassGroupName, setEditClassGroupName] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [res, studentsRes] = await Promise.all([getClassGroups(), getStudents("current", true)]);
      setClassGroups(res.data);
      setAllStudents(studentsRes.data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-6" data-testid="class-groups-tab">
      <CoreClassesSection />
      <RubricImageSection />
      <Card>
        <CardHeader>
          <CardTitle>Morning & Afternoon Class Groups</CardTitle>
          <CardDescription>Configure group names, images, and assign students to morning and afternoon groups.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : (
            <div className="space-y-8">
              <GroupSection period="morning" groups={classGroups.morning_groups} allStudents={allStudents}
                editingClassGroup={editingClassGroup} setEditingClassGroup={setEditingClassGroup}
                editClassGroupName={editClassGroupName} setEditClassGroupName={setEditClassGroupName}
                classGroups={classGroups} fetchData={fetchData} />
              <hr />
              <GroupSection period="afternoon" groups={classGroups.afternoon_groups} allStudents={allStudents}
                editingClassGroup={editingClassGroup} setEditingClassGroup={setEditingClassGroup}
                editClassGroupName={editClassGroupName} setEditClassGroupName={setEditClassGroupName}
                classGroups={classGroups} fetchData={fetchData} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
